import { ParamsProps } from "components/live-event/live-event";
import { GetSessionStaticVideos } from "connection/session-static-videos";
import { useSocket } from "connection/socket";
import { getVideo } from "connection/videos";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTypedSelector } from "store/reducers/use-typed-selector";
import { Session, StaticVideo } from "types/working-model";
import { useGetEvent } from "./session.hooks";
import { useIsAdmin } from "./context.hooks";
import { showAlert } from "@general-ui/alert/alert-service";
import { useIsMounted } from "./use-is-mounted";

export const useSessionStaticVideos = (session: Session | null, language: string) => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const eventUuid = useGetEvent()?.uuid;
	const isAdmin = useIsAdmin();

	const [loadingVideos, setLoadingVideos] = useState(false);
	const [videoMap, setVideoMap] = useState<Record<string, StaticVideo>>({}); // Language -> StaticVideo
	const videoMapRef = useRef(videoMap);
	videoMapRef.current = videoMap;
	const [failedToLoadVideos, setFailedToLoadVideos] = useState<string[]>([]);
	const failedToLoadVideosRef = useRef(failedToLoadVideos);
	failedToLoadVideosRef.current = failedToLoadVideos;
	// This ref prevents hitting the lamdba on event publish since we don't want every user to do that at once
	const fetchedSessionUuid = useRef<string | null>(null);
	const isFetching = useRef(false);
	const isMounted = useIsMounted();
	const { sessionUuid } = useParams() as unknown as ParamsProps;

	const socket = useSocket(`session-${sessionUuid || session?.uuid}`);

	useEffect(() => {
		const fetchStaticVideoMap = async () => {
			if (!eventUuid || !session?.uuid || isFetching.current) return;
			isFetching.current = true;
			setLoadingVideos(true);
			try {
				// Only use missing videos for admin/editor
				const missingVideos = videosHasUpdated(session.static_videos ?? {}, videoMapRef.current, failedToLoadVideosRef.current);
				if (isAdmin && missingVideos) {
					if (!session.static_videos || !token || !user) return;
					const videos: Record<string, StaticVideo> = {};
					fetchedSessionUuid.current = session.uuid;
					for (const [language, uuid] of Object.entries(session.static_videos)) {
						try {
							const video = await getVideo(token, user.active_channel, uuid);
							videos[language] = video;
						} catch {
							showAlert({
								message: `Failed to fetch on demand video for language ${language}`,
								type: 'error',
								duration: 5000,
							});
							setFailedToLoadVideos((prev) => [...prev, uuid]);
						}
					}
					setVideoMap(videos);
				} else if (session.uuid != fetchedSessionUuid.current) {
					const videos = await GetSessionStaticVideos(eventUuid, session.uuid);
					if (!isMounted.current) return;
					setVideoMap(videos);
					fetchedSessionUuid.current = session.uuid;
				}
			} finally {
				isFetching.current = false;
				if (isMounted.current) {
					setLoadingVideos(false);
				}
			}
		};

		fetchStaticVideoMap();

		// IMPORTANT re deps array:
		// Only re-run when the inputs change (session uuid/static_videos, token, user, admin status, eventUuid), not when its own outputs change.
		// The isFetching ref also guards against concurrent runs, but the dep array omission is what prevents the re-trigger cycle
	}, [session?.uuid, session?.static_videos, token, user, isAdmin, eventUuid, isMounted]);


	// Clear out failedToLoadVideos if session changes
	useEffect(() => {
		if (fetchedSessionUuid.current && fetchedSessionUuid.current !== session?.uuid) {
			setFailedToLoadVideos([]);
		}
	}, [session?.uuid]);


	useEffect(() => {
		const updateStaticVideos = ({ data }: { data: Record<string, StaticVideo> }) => {
			if (data && !isAdmin) {
				setVideoMap(data);
			}
		};
		socket.addListener('static-videos-updated', updateStaticVideos);
		return () => {
			socket.removeListener('static-videos-updated', updateStaticVideos);
		};
	}, [isAdmin, socket]);

	let currentVideo;
	let failedToLoadVideo = false;

	const lang = session?.streaming_options.single_stream ? session?.default_language : language;

	if (session) {
		currentVideo = videoMap[lang] ?? videoMap[session?.default_language] ?? null;
		const videoUuid = session.static_videos?.[lang] ?? session.static_videos?.[session?.default_language] ?? null;
		failedToLoadVideo = !!videoUuid && failedToLoadVideos.includes(videoUuid);
	}

	// note loadingVideos is not currently consumed, but will leave in anyhow
	return {
		loadingVideos,
		currentVideo,
		failedToLoadVideo
	};
};


const videosHasUpdated = (staticVideos: Record<string, string>, videoMap: Record<string, StaticVideo>, failedToLoadVideos: string[]): boolean => {
	for (const [language, uuid] of Object.entries(staticVideos ?? {})) {
		const staticVideo = videoMap[language];
		if (staticVideo?.uuid !== uuid && !failedToLoadVideos.includes(uuid)) {
			return true;
		}
	}
	return false;
};
